// ============================================================
// SettingsView — unified settings hub.
// ------------------------------------------------------------
// Mobile-first design:
//   • Mobile: clean list of category rows (iOS/Android Settings
//     pattern). Tapping a row navigates into that section
//     inline (the list is replaced by the section content + a
//     back button).
//   • Desktop (sm+): horizontal tab bar (grid-cols-6) — enough
//     horizontal space for all 6 tabs in a single row.
//
// Sections:
//   1. Appearance — theme (light/dark/system)
//   2. Language   — i18n language picker
//   3. Notifications — push/email/marketing prefs
//   4. Privacy    — sessions, devices, data export
//   5. App        — PWA install / update / clear cache
//   6. About      — version + legal
// ============================================================

import { useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { LanguageSwitcher } from "@/components/i18n/language-switcher";
import { SyncIndicator } from "@/components/i18n/sync-indicator";
import { PageHeader } from "@/components/features/states";
import {
  ArrowLeft,
  Sun,
  Moon,
  Monitor,
  Languages,
  Bell,
  ShieldCheck,
  Smartphone,
  Info,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { AppManagementCard, AboutCard } from "@/components/views/settings-sections";

const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "fr", label: "Français", native: "Français" },
  { code: "ar", label: "Arabic", native: "العربية" },
  { code: "aa", label: "Afar", native: "Qafar" },
  { code: "am", label: "Amharic", native: "አማርኛ" },
  { code: "so", label: "Somali", native: "Soomaali" },
  { code: "om", label: "Oromo", native: "Afaan Oromoo" },
  { code: "sw", label: "Swahili", native: "Kiswahili" },
  { code: "ti", label: "Tigrinya", native: "ትግርኛ" },
];

// localStorage keys for notification prefs (offline-friendly).
const NOTIF_KEYS = {
  push: "horndeliver.notif.push",
  email: "horndeliver.notif.email",
  jobUpdates: "horndeliver.notif.jobUpdates",
  marketing: "horndeliver.notif.marketing",
};

function getBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}
function setBool(key: string, v: boolean) {
  try {
    localStorage.setItem(key, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function SettingsView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("appearance");
  // Mobile: which section is open. null = show the category list.
  const [mobileSection, setMobileSection] = useState<string | null>(null);

  // Notification prefs — persisted to localStorage for offline PWA use.
  const [pushOn, setPushOn] = useState(() => getBool(NOTIF_KEYS.push, true));
  const [emailOn, setEmailOn] = useState(() => getBool(NOTIF_KEYS.email, true));
  const [jobOn, setJobOn] = useState(() => getBool(NOTIF_KEYS.jobUpdates, true));
  const [mktOn, setMktOn] = useState(() => getBool(NOTIF_KEYS.marketing, false));

  if (!user) return null;

  const themeOptions: { value: "light" | "dark" | "system"; label: string; icon: LucideIcon }[] = [
    { value: "light", label: t("theme.light"), icon: Sun },
    { value: "dark", label: t("theme.dark"), icon: Moon },
    { value: "system", label: t("theme.system"), icon: Monitor },
  ];

  // Category list config — shared by the mobile list and the desktop tabs.
  const categories: { id: string; label: string; icon: LucideIcon; desc: string }[] = [
    { id: "appearance", label: t("settings.appearance"), icon: Sun, desc: t("settings.appearanceDesc") },
    { id: "language", label: t("settings.language"), icon: Languages, desc: t("settings.languageDesc") },
    { id: "notifications", label: t("settings.notifications"), icon: Bell, desc: t("settings.notificationsDesc") },
    { id: "privacy", label: t("settings.privacy"), icon: ShieldCheck, desc: t("settings.privacyDesc") },
    { id: "app", label: t("settings.app"), icon: Smartphone, desc: t("settings.appDesc") || "Install, update, clear cache" },
    { id: "about", label: t("settings.about"), icon: Info, desc: t("settings.aboutDesc") || "Version & legal" },
  ];

  return (
    <div className="space-y-4">
      <PageHeader title={t("nav.settings")} subtitle={t("settings.subtitle")} />

      {/* ==================================================== */}
      {/* MOBILE: list-based category navigation.              */}
      {/* ==================================================== */}
      <div className="sm:hidden">
        {mobileSection === null ? (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border/60">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setMobileSection(cat.id)}
                      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-muted/40"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{cat.label}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{cat.desc}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Back to category list */}
            <button
              onClick={() => setMobileSection(null)}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> {t("nav.settings")}
            </button>
            {mobileSection === "appearance" && <AppearanceSection theme={theme} setTheme={setTheme} themeOptions={themeOptions} />}
            {mobileSection === "language" && <LanguageSection />}
            {mobileSection === "notifications" && (
              <NotificationsSection
                pushOn={pushOn} emailOn={emailOn} jobOn={jobOn} mktOn={mktOn}
                setPushOn={(v) => { setPushOn(v); setBool(NOTIF_KEYS.push, v); }}
                setEmailOn={(v) => { setEmailOn(v); setBool(NOTIF_KEYS.email, v); }}
                setJobOn={(v) => { setJobOn(v); setBool(NOTIF_KEYS.jobUpdates, v); }}
                setMktOn={(v) => { setMktOn(v); setBool(NOTIF_KEYS.marketing, v); }}
              />
            )}
            {mobileSection === "privacy" && <PrivacySection navigate={navigate} />}
            {mobileSection === "app" && <AppManagementCard />}
            {mobileSection === "about" && <AboutCard />}
          </div>
        )}
      </div>

      {/* ==================================================== */}
      {/* DESKTOP (sm+): horizontal tab bar.                   */}
      {/* ==================================================== */}
      <div className="hidden sm:block">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-6">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs lg:text-sm">
                  <Icon className="mr-1.5 h-3.5 w-3.5" />
                  <span className="hidden lg:inline">{cat.label}</span>
                  <span className="lg:hidden">{cat.label.split(" ")[0]}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="appearance" className="space-y-4">
            <AppearanceSection theme={theme} setTheme={setTheme} themeOptions={themeOptions} />
          </TabsContent>
          <TabsContent value="language" className="space-y-4">
            <LanguageSection />
          </TabsContent>
          <TabsContent value="notifications" className="space-y-4">
            <NotificationsSection
              pushOn={pushOn} emailOn={emailOn} jobOn={jobOn} mktOn={mktOn}
              setPushOn={(v) => { setPushOn(v); setBool(NOTIF_KEYS.push, v); }}
              setEmailOn={(v) => { setEmailOn(v); setBool(NOTIF_KEYS.email, v); }}
              setJobOn={(v) => { setJobOn(v); setBool(NOTIF_KEYS.jobUpdates, v); }}
              setMktOn={(v) => { setMktOn(v); setBool(NOTIF_KEYS.marketing, v); }}
            />
          </TabsContent>
          <TabsContent value="privacy" className="space-y-4">
            <PrivacySection navigate={navigate} />
          </TabsContent>
          <TabsContent value="app" className="space-y-4">
            <AppManagementCard />
          </TabsContent>
          <TabsContent value="about" className="space-y-4">
            <AboutCard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================
// Section components — extracted so both mobile and desktop
// render the exact same content.
// ============================================================
function AppearanceSection({
  theme,
  setTheme,
  themeOptions,
}: {
  theme: string | undefined;
  setTheme: (v: "light" | "dark" | "system") => void;
  themeOptions: { value: "light" | "dark" | "system"; label: string; icon: LucideIcon }[];
}) {
  const { t } = useTranslation();
  return (
    <>
      <Card>
        <CardContent className="p-5">
          <h3 className="text-base font-semibold">{t("theme.label")}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t("settings.appearanceDesc")}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {themeOptions.map((opt) => {
              const Icon = opt.icon;
              const active = (theme || "system") === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setTheme(opt.value)}
                  className={`flex flex-col items-center gap-2 rounded-xl border p-4 text-sm font-medium transition ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold">
                {t("settings.syncStatus")}
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t("settings.syncDesc")}
              </p>
            </div>
            <SyncIndicator />
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function LanguageSection() {
  const { t, i18n } = useTranslation();
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-base font-semibold">{t("language.label")}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("settings.languageDesc")}
        </p>
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {LANGUAGES.map((lang) => {
            const active = i18n.language?.startsWith(lang.code);
            return (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`flex items-center justify-between rounded-lg border p-3 text-left transition ${
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/40"
                }`}
              >
                <div>
                  <p className="text-sm font-semibold">{lang.native}</p>
                  <p className="text-xs text-muted-foreground">{lang.label}</p>
                </div>
                {active && <Badge variant="secondary">●</Badge>}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3">
          <span className="text-sm">{t("settings.quickSwitch")}</span>
          <LanguageSwitcher />
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationsSection({
  pushOn, emailOn, jobOn, mktOn,
  setPushOn, setEmailOn, setJobOn, setMktOn,
}: {
  pushOn: boolean; emailOn: boolean; jobOn: boolean; mktOn: boolean;
  setPushOn: (v: boolean) => void;
  setEmailOn: (v: boolean) => void;
  setJobOn: (v: boolean) => void;
  setMktOn: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-5 space-y-1">
        <h3 className="text-base font-semibold">
          {t("settings.notifications")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("settings.notificationsDesc")}
        </p>
        <div className="mt-3 divide-y divide-border/60">
          <NotifRow icon={Bell} label={t("settings.pushNotif")} desc={t("settings.pushNotifDesc")} checked={pushOn} onChange={setPushOn} />
          <NotifRow icon={Bell} label={t("settings.emailNotif")} desc={t("settings.emailNotifDesc")} checked={emailOn} onChange={setEmailOn} />
          <NotifRow icon={Bell} label={t("settings.jobUpdates")} desc={t("settings.jobUpdatesDesc")} checked={jobOn} onChange={setJobOn} />
          <NotifRow icon={Bell} label={t("settings.marketing")} desc={t("settings.marketingDesc")} checked={mktOn} onChange={setMktOn} />
        </div>
      </CardContent>
    </Card>
  );
}

function PrivacySection({ navigate }: { navigate: (path: string) => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-base font-semibold">
          {t("settings.privacy")}
        </h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("settings.privacyDesc")}
        </p>
        <div className="mt-3 divide-y divide-border/60">
          <SettingsRow label={t("settings.sessions")} desc={t("settings.sessionsDesc")} onClick={() => navigate("app/profile")} />
          <SettingsRow label={t("profile.idVerification")} desc={t("settings.trustDesc")} onClick={() => navigate("app/trust")} />
          <SettingsRow label={t("settings.dataProtection")} desc={t("settings.dataProtectionDesc")} onClick={() => navigate("legal/data-protection")} />
          <SettingsRow label={t("settings.privacyPolicy")} desc={t("settings.privacyPolicyDesc")} onClick={() => navigate("legal/privacy")} />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Helper components
// ============================================================
function NotifRow({
  icon: Icon,
  label,
  desc,
  checked,
  onChange,
}: {
  icon: LucideIcon;
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold">{label}</p>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}

function SettingsRow({
  label,
  desc,
  onClick,
}: {
  label: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between py-3 text-left transition hover:bg-muted/40"
    >
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}
